import type {
  CommitStrokeRequest,
  CommitStrokeResponse,
  CommitStrokesBatchRequest,
  CommitStrokesBatchResponse,
  GetStateResponse,
  ClearCanvasResponse,
  GetChannelConfigResponse,
  StrokeInput,
  AnimatedDrawing,
  GetPendingSubmissionsResponse,
  ModerationActionResponse,
} from '@twitch-draw/shared';
import { Sentry } from './sentry';

const EBS_URL = import.meta.env.VITE_EBS_URL || 'http://localhost:3000';
const REQUEST_TIMEOUT = 10000;

class EBSClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${this.token}`,
      };
      if (options.body) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${EBS_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      Sentry.captureException(err);
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw err;
    }
  }

  async getState(): Promise<GetStateResponse> {
    return this.request<GetStateResponse>('/state');
  }

  async commitStroke(
    stroke: StrokeInput,
    transactionId: string,
    transactionReceipt: string,
    displayName?: string
  ): Promise<CommitStrokeResponse> {
    const body: CommitStrokeRequest = {
      stroke,
      transactionId,
      transactionReceipt,
      displayName,
    };

    return this.request<CommitStrokeResponse>('/stroke', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async commitStrokesBatch(
    strokes: StrokeInput[],
    transactionId: string,
    transactionReceipt: string,
    displayName?: string,
    animation?: AnimatedDrawing
  ): Promise<CommitStrokesBatchResponse> {
    const body: CommitStrokesBatchRequest = {
      strokes,
      transactionId,
      transactionReceipt,
      displayName,
      ...(animation ? { animation } : {}),
    };

    return this.request<CommitStrokesBatchResponse>('/strokes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async clearCanvas(): Promise<ClearCanvasResponse> {
    return this.request<ClearCanvasResponse>('/clear', {
      method: 'POST',
    });
  }

  async getChannelConfig(): Promise<GetChannelConfigResponse> {
    return this.request<GetChannelConfigResponse>('/channel-config');
  }

  async getPendingSubmissions(): Promise<GetPendingSubmissionsResponse> {
    return this.request<GetPendingSubmissionsResponse>('/submissions/pending');
  }

  async approveSubmission(submissionId: string): Promise<ModerationActionResponse> {
    return this.request<ModerationActionResponse>(`/submissions/${submissionId}/approve`, {
      method: 'POST',
    });
  }

  async rejectSubmission(submissionId: string): Promise<ModerationActionResponse> {
    return this.request<ModerationActionResponse>(`/submissions/${submissionId}/reject`, {
      method: 'POST',
    });
  }

  async getChannelDisplayName(): Promise<{ displayName: string }> {
    return this.request<{ displayName: string }>('/channel-display-name');
  }
}

export const ebsClient = new EBSClient();
